import { Info } from "lucide-react";
import { Panel } from "@/components/ui/Panel";
import type { FileRejection } from "react-dropzone";
import { FileDropZone } from "@/components/shared/FileDropZone";
import { Button } from "@/components/ui/button";
import { PRIOR_ART_MEDIA_NOTICE } from "@/lib/constants";
import { describeRejection } from "@/components/shared/UploadComponent";
import type { IntakeComponentSpec } from "@/lib/intake-components";
import type { PendingUpload } from "@/hooks/use-slot-uploads";
import type { LiteratureType } from "@patent-studio/api-types";

/** The two zones, one per literature type, in the order the design draws them. */
const ZONES: readonly { type: LiteratureType; title: string }[] = [
  { type: "patent_literature", title: "Patent literature" },
  { type: "non_patent_literature", title: "Non-patent literature" },
];

/** The standing notice under the drop zones: information, never an error, never dismissed. */
function MediaNotice() {
  return (
    <Panel
      as="p"
      role="note"
      data-testid="prior-art-media-notice"
      shape="row"
      pad="tight"
      tone="info"
      className="mt-3 flex items-start gap-2 py-2.5 text-[12px] leading-relaxed"
    >
      <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
      <span>{PRIOR_ART_MEDIA_NOTICE}</span>
    </Panel>
  );
}

/**
 * A file the server refused, kept on the screen with its reason.
 *
 * The file itself is still held, so trying again is one click rather than
 * finding the document a second time. Discarding it lets the attorney give up.
 */
function RefusedRow({
  row,
  onRetry,
  onDiscard,
}: {
  row: PendingUpload;
  onRetry: (id: string) => void;
  onDiscard: (id: string) => void;
}) {
  const sending = row.status === "sending";
  return (
    <Panel
      as="li"
      shape="row"
      pad="tight"
      tone="destructive"
      data-testid={`prior-art-refused-${row.fileName}`}
      className="mb-1.5"
    >
      {/* The tone colours the surface, and a surface's text colour cascades:
          without this the file's name and both controls read red, and the
          ghost button's "Discard" would go grey only under the pointer. The
          refusal itself says it in red, below. */}
      <div className="flex items-center gap-2.5 text-foreground">
        <span className="truncate text-detail font-semibold">
          {row.fileName}
        </span>
        <span className="flex-1" />
        <Button
          type="button"
          size="dense"
          variant="secondary"
          disabled={sending}
          onClick={() => onRetry(row.id)}
          className="cursor-pointer"
        >
          {sending ? "Trying again…" : "Try again"}
        </Button>
        <Button
          type="button"
          size="dense"
          variant="quiet"
          disabled={sending}
          onClick={() => onDiscard(row.id)}
          className="cursor-pointer"
        >
          Discard
        </Button>
      </div>
      {row.message && (
        <p className="mt-1 text-[11.5px] text-destructive">{row.message}</p>
      )}
    </Panel>
  );
}

/**
 * The two drop zones, patent and non-patent literature, and the notice under
 * them. A document dropped on one is that literature, so the attorney types
 * it by where they put it.
 *
 * Each takes what the intake slot takes and refuses what it refuses, in the
 * same words, because a document carried through from intake sits in this
 * same list. The gate is the spec's `accept`; the labels are what the
 * attorney reads.
 */
export function PriorArtUpload({
  spec,
  busy,
  refused,
  error,
  onFilesSelected,
  onRejected,
  onRetry,
  onDiscard,
}: {
  spec: IntakeComponentSpec;
  busy: boolean;
  refused: readonly PendingUpload[];
  error: string | null;
  onFilesSelected: (files: File[], literatureType: LiteratureType) => void;
  onRejected: (message: string) => void;
  onRetry: (id: string) => void;
  onDiscard: (id: string) => void;
}) {
  return (
    <div data-testid="prior-art-upload">
      <div className="grid gap-3 sm:grid-cols-2">
        {ZONES.map((zone) => (
          <FileDropZone
            key={zone.type}
            accept={spec.accept}
            inputLabel={`Upload ${zone.title.toLowerCase()}`}
            title={zone.title}
            hint={
              busy
                ? "Uploading…"
                : `Drag & drop, or browse · ${spec.acceptedLabels.join(", ")}`
            }
            action="Add more"
            onDrop={(files) => onFilesSelected(files, zone.type)}
            onRejected={(rejections: FileRejection[]) =>
              onRejected(
                describeRejection(spec.title, spec.acceptedLabels, rejections),
              )
            }
          />
        ))}
      </div>
      <MediaNotice />
      {error && (
        <Panel
          as="p"
          shape="row"
          pad="tight"
          tone="destructive"
          role="alert"
          className="mt-2.5 text-[11.5px]"
        >
          {error}
        </Panel>
      )}
      {refused.length > 0 && (
        <ul className="mt-2.5">
          {refused.map((row) => (
            <RefusedRow
              key={row.id}
              row={row}
              onRetry={onRetry}
              onDiscard={onDiscard}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
